import { createContext, useState, useEffect, useCallback, ReactNode, useRef, useMemo } from 'react';
import { api, type User } from '../lib/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { name: string; email: string; password: string; password_confirmation: string; phone?: string }) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: { name?: string; phone?: string }) => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  initialUser?: User | null;
  /** SSR уже проверил /auth/me — не дергаем API снова (убирает 401/лишний запрос у гостей) */
  initialAuthChecked?: boolean;
}

export function AuthProvider({
  children,
}: AuthProviderProps) {
  // Сессию всегда подтверждаем через клиентский /auth/me.
  // SSR-user не используем: раньше общий кэш /auth/me подставлял чужой аккаунт.
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isRefreshingRef = useRef(false);

  const refreshUser = useCallback(async (force = false) => {
    // Предотвращаем параллельные запросы
    if (isRefreshingRef.current && !force) {
      return;
    }

    isRefreshingRef.current = true;
    try {
      const response = await api.auth.me();
      setUser(response.user ?? null);
      return true;
    } catch (error: any) {
      if (error?.status === 401) {
        setUser(null);
      }
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Всегда перепроверяем сессию на клиенте.
    // SSR раньше кэшировал /auth/me одним ключом → чужой user в __INITIAL_STATE__
    // или user=null при живой cookie; HttpOnly cookie нельзя прочитать из JS.
    refreshUser(true)
      .catch(() => {
        /* 401/сеть — гость */
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.auth.login(email, password);
    setUser(response.user);
    setIsLoading(false);
  }, []);

  const register = useCallback(async (data: { name: string; email: string; password: string; password_confirmation: string; phone?: string }) => {
    const response = await api.auth.register(data);
    setUser(response.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // ignore
    } finally {
      setUser(null);
    }
  }, []);

  const updateProfile = useCallback(async (data: { name?: string; phone?: string }) => {
    const response = await api.auth.updateProfile(data);
    setUser(response.user);
  }, []);

  const value: AuthContextType = useMemo(() => ({
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateProfile,
    refreshUser,
  }), [user, isLoading, login, register, logout, updateProfile, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
